// Blueprint's Customers in words (pages/ux-full screens/customers.js): what the list says beside each
// name, its show filters, and a record's "What's open", from what customers.list and customers.get
// answer. Every field may be missing (an older server, a role that doesn't see it): then it says nothing.
import type { CustomerTableRow } from '@flowology/ui-web/fl';
import { jobStatusWord } from '@flowology/ui-web/words';

/** customers.list's `show`: everyone, or only those. Owe money only for those who see every invoice. */
export type ListShow = 'all' | 'homes' | 'businesses' | 'members' | 'owing';
export const LIST_SHOWS: readonly [ListShow, string][] = [
  ['all', 'Everyone'],
  ['homes', 'Homes'],
  ['businesses', 'Businesses'],
  ['members', 'Members'],
  ['owing', 'Owe money'],
];

/** One customer as customers.list answers. */
export interface ListRow {
  id: string;
  number: string;
  displayName: string;
  city: string | null;
  kind?: 'residential' | 'commercial';
  phone?: string | null;
  address?: { line1: string; city: string } | null;
  membership?: { name: string; endsOn: string } | null;
  owing?: { cents: number; overdue: boolean } | null;
  open?: { jobs: number; working: boolean; estimates: number } | null;
  lastVisitOn?: string | null;
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** "2 jobs (one under way), 1 estimate"; null with nothing open, or when the server doesn't say. */
export function openText(open: ListRow['open']): string | null {
  if (!open || (!open.jobs && !open.estimates)) return null;
  const jobs = open.jobs ? `${plural(open.jobs, 'job')}${open.working ? ' (one under way)' : ''}` : null;
  const ests = open.estimates ? plural(open.estimates, 'estimate') : null;
  return [jobs, ests].filter(Boolean).join(', ');
}

/** Whether this caller is told what people owe: a list where any row says it. */
export const seesOwing = (rows: readonly ListRow[]): boolean => rows.some((r) => r.owing != null);

/** What's open with a customer, as customers.get answers it. */
export interface CustomerOpen {
  jobs: {
    count: number;
    listed: {
      id: string;
      number: string;
      summary: string;
      status: 'open' | 'in_progress' | 'awaiting_review';
      nextVisit: { start: string; end: string; status: string; techs: string[] } | null;
    }[];
  };
  estimates: {
    count: number;
    listed: {
      id: string;
      number: string;
      title: string;
      jobId: string;
      status: 'presented' | 'sent';
      presentedAt: string | null;
    }[];
  };
}

/** The plan a customer is on now, as customers.get answers. */
export interface MembershipCard {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
  autoRenew: boolean;
  visitsTotal: number;
  visitsUsed: number;
}

/** A row of What's open: what it is, its number, its words, its state (DESIGN.md §2) and its job. */
export interface OpenItem {
  key: string;
  kind: 'Job' | 'Estimate';
  ref: string;
  text: string;
  techs: string[];
  state: string;
  /** now: under way, drawn in ink; wait: waiting on someone, dashed; muted: everything else. */
  tone: 'now' | 'wait' | 'muted';
  jobId: string;
}

/** The jobs first, then the estimates waiting on them; `when` says a visit's day and window. */
export function openItems(open: CustomerOpen, when: (start: string, end: string) => string): OpenItem[] {
  const jobs: OpenItem[] = open.jobs.listed.map((j) => ({
    key: `j-${j.id}`,
    kind: 'Job',
    ref: `#${j.number}`,
    text: j.nextVisit ? `${j.summary}. Visit ${when(j.nextVisit.start, j.nextVisit.end)}` : j.summary,
    techs: j.nextVisit?.techs ?? [],
    // the app's one word for where a job stands (job-words); the tone stays this list's own
    state: jobStatusWord(j.status, { unscheduled: !j.nextVisit }),
    tone:
      j.status === 'in_progress'
        ? ('now' as const)
        : j.status === 'awaiting_review' || j.nextVisit
          ? ('muted' as const)
          : ('wait' as const),
    jobId: j.id,
  }));
  const estimates: OpenItem[] = open.estimates.listed.map((e) => ({
    key: `e-${e.id}`,
    kind: 'Estimate',
    ref: e.number,
    text: e.title,
    techs: [],
    state: e.status === 'sent' ? 'Sent, waiting on them' : 'Shown, waiting on them',
    tone: 'wait',
    jobId: e.jobId,
  }));
  return [...jobs, ...estimates];
}

/** "1 visit left of 2 this term", or "All visits used of 2". */
export function visitsLeftText(m: { visitsTotal: number; visitsUsed: number }): string {
  const left = Math.max(0, m.visitsTotal - m.visitsUsed);
  if (!m.visitsTotal) return 'No visits included';
  return left
    ? `${plural(left, 'visit')} left of ${m.visitsTotal} this term`
    : `All visits used of ${m.visitsTotal} this term`;
}

/** "13 years old", "Under a year old"; null when its install day isn't known. */
export function ageText(ageYears: number | null | undefined): string | null {
  if (ageYears == null) return null;
  return ageYears < 1 ? 'Under a year old' : `${plural(ageYears, 'year')} old`;
}

/** A day as the list says it: "Oct 21, 2026". */
export const listDay = (d: string): string =>
  new Intl.DateTimeFormat('en-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${d}T12:00:00Z`));

/**
 * customers.list's rows as All customers' table says them (ui-web customerColumns): the number and home
 * or business under the name, the phone as it's read, the street and city, the plan, what's owed and
 * whether it's overdue, what's open and the last visit's day.
 */
export function customerTableRows(
  rows: readonly ListRow[],
  words: { phone: (e164: string) => string; money: (cents: number) => string },
): CustomerTableRow[] {
  return rows.map((c) => ({
    id: c.id,
    name: c.displayName,
    meta: `${c.number}${c.kind ? `, ${c.kind === 'commercial' ? 'business' : 'home'}` : ''}`,
    ...(c.phone ? { phone: words.phone(c.phone) } : {}),
    ...(c.address ? { address: `${c.address.line1}, ${c.address.city}` } : c.city ? { address: c.city } : {}),
    member: c.membership ? c.membership.name : null,
    owes:
      c.owing && c.owing.cents > 0 ? { amount: words.money(c.owing.cents), overdue: c.owing.overdue } : null,
    ...(openText(c.open) ? { open: openText(c.open)! } : {}),
    ...(c.lastVisitOn ? { lastVisit: listDay(c.lastVisitOn) } : {}),
  }));
}
